import type { MeasureValue, ReportContext } from "../../../types/stationPerformance";
import { COCHARGE_REVENUE_TEXTS, cochargeFeeLabel } from "../StationPerformance.consts";

/** Escapes one cell. Doubles embedded quotes, matching the repo's existing export. */
export const cell = (value: unknown): string => `"${String(value ?? "").replace(/"/g, '""')}"`;

/**
 * Escapes one cell of free text that came from outside (names, e-mails, station IDs).
 *
 * Quoting alone does not stop Excel from evaluating a value that starts with `=`, `+`, `-`, `@`, a tab or
 * a carriage return: an owner named `=HYPERLINK(...)` would run as a formula when the file is opened. A
 * leading apostrophe makes Excel read it as text. Never use this for numbers: a negative figure also
 * starts with `-`.
 */
export const textCell = (value: unknown): string => {
  const text = String(value ?? "");
  return cell(/^[=+\-@\t\r]/.test(text) ? `'${text}` : text);
};

/**
 * A measure in a CSV cell.
 *
 * Undefined measures export as **empty**, never as `0`. Same rule as on screen, and it matters more here:
 * a spreadsheet will happily average a column of zeroes and produce a confidently wrong number, whereas a
 * blank is skipped. The reason travels in a companion column so the blank stays explicable.
 */
export const measureCell = (measure: MeasureValue | null | undefined): string =>
  measure && measure.status === "OK" && measure.value !== null ? cell(measure.value) : cell("");

export const measureReasonCell = (measure: MeasureValue | null | undefined): string =>
  measure && measure.status === "NOT_APPLICABLE" ? cell(measure.reason ?? "NOT_APPLICABLE") : cell("");

/**
 * Header rows recording every setting the figures were produced under (FR-035).
 *
 * Without these an exported file is uninterpretable a week later: per site or per connector, total or
 * cocharge share, free charging in or out, computed when. A spreadsheet outlives the screen it came from,
 * so the context has to travel with it.
 */
export const contextHeaderRows = (
  title: string,
  context: ReportContext | null | undefined,
): string[][] => {
  const rows: string[][] = [[cell(title)]];
  if (!context) return [...rows, []];
  const o = context.options;
  rows.push([cell("Reporting unit"), cell(o?.unit === "SITE" ? "Per site" : "Per connector")]);
  rows.push([cell("Session segment"), cell(o?.segment ?? "")]);
  rows.push([
    cell("Money basis"),
    cell(
      o?.moneyView === "COCHARGE_SHARE" ? COCHARGE_REVENUE_TEXTS.shareLabel : "Total taken",
    ),
  ]);
  if (o?.moneyView === "COCHARGE_SHARE") {
    rows.push([cell("Fee rate"), cell(cochargeFeeLabel(context))]);
  }
  rows.push([cell("Free charging included"), cell(o?.includeFreeCharge === false ? "No" : "Yes")]);
  if (o?.station) rows.push([cell("Station filter"), cell(o.station)]);
  if (o?.provider) rows.push([cell("Channel filter"), cell(o.provider)]);
  rows.push([cell("Data cut-off"), cell(context.dataCutOff ?? "unknown")]);
  rows.push([
    cell("Month closure"),
    cell(
      Object.entries(context.monthClosure ?? {})
        .map(([m, state]) => `${m}=${state}`)
        .join(" "),
    ),
  ]);
  if (context.liveDayIncluded) {
    rows.push([cell("Includes today"), cell("Yes — figures move during the day")]);
  }
  if (context.staleMonths?.length) {
    rows.push([cell("Not yet settled"), cell(context.staleMonths.join(" "))]);
  }
  rows.push([
    cell("Note"),
    cell(
      "Money is cash actually taken. Free charging is never assigned a monetary value, so money understates units that shifted to free charging — read it beside the free share.",
    ),
  ]);
  rows.push([
    cell("Note"),
    cell("Empty cells mean the measure is not applicable, not zero. See the accompanying reason column."),
  ]);
  rows.push([]);
  return rows;
};

/**
 * Serialises pre-escaped rows and hands the file to the browser.
 *
 * `bom` prefixes a UTF-8 byte-order mark. Without it, Excel on Windows reads the file as Windows-1252 and
 * turns names such as "Hüser" into "HÃ¼ser". `separator` lets a file meant for German-locale Excel use
 * `;`, the list separator Excel expects there. Both default to the existing behaviour, so current exports
 * stay byte-identical.
 */
export const downloadCsv = (
  filename: string,
  rows: string[][],
  { bom = false, separator = "," }: { bom?: boolean; separator?: "," | ";" } = {},
): void => {
  const content = (bom ? "\uFEFF" : "") + rows.map((row) => row.join(separator)).join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
